import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wallet,
  Heart,
  Activity,
  ShieldCheck,
  FileText,
  Globe2,
  Users,
  ArrowRight,
  Check,
  BarChart3,
} from "lucide-react";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const fundingSources = ["Donors", "NGOs", "Foundations", "Governments", "Corporate CSR", "Development Agencies"];
const supportedGroups = [
  "Women and vulnerable groups",
  "Low-income citizens",
  "Youth & students",
  "Rural communities",
  "Human rights cases",
  "Public-interest matters",
];

const HeroBlock = () => (
  <section className="relative pt-32 pb-24 overflow-hidden">
    <div className="absolute inset-0 opacity-50"><NeuralNetworkBackground density={22} /></div>
    <ScaleWatermark />
    <div className="gold-aura -top-32 -right-32 opacity-60" />
    <div className="grain-overlay" />
    <div className="container relative z-10 mx-auto px-6 max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
        <div className="aj-chip aj-chip-gold mb-6"><Wallet className="w-3 h-3" /> Justice Wallet · For NGOs & Donors</div>
        <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05]">
          Funding justice for those who{" "}
          <span className="italic text-gold-gradient">cannot afford it.</span>
        </h1>
        <p className="font-inter text-base md:text-lg text-white/65 mt-6 max-w-2xl leading-relaxed">
          The Justice Wallet is a transparent funding mechanism that lets donors, NGOs, foundations and
          development partners sponsor legal support — with verified lawyers, milestone-based fund release,
          and impact reporting aligned to SDG 16.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/contact" data-testid="ngos-cta-fund" className="btn-aj-primary">
            Fund the Justice Wallet <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" data-testid="ngos-cta-partner" className="btn-aj-secondary">
            Partner With Us
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const ForNGOs = () => (
  <div className="bg-ink text-white" data-testid="for-ngos-page">
    <HeroBlock />

    {/* Funding sources */}
    <Section eyebrow="Funding Sources" title={<>Capital from every corner of the <span className="italic text-aj-gradient">justice ecosystem.</span></>}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {fundingSources.map((s, i) => (
          <motion.div key={s} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
            <GlassCard glow className="text-center">
              <Heart className="w-7 h-7 text-gold-300 mx-auto mb-3" />
              <div className="font-inter text-sm text-white/85">{s}</div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Who we support */}
    <Section eyebrow="Beneficiaries" title={<>Who the wallet <span className="italic text-aj-gradient">stands for.</span></>} subtitle="Eligibility is assessed against income level, vulnerability category, case type, urgency and supporting documents — with NGO referrals respected.">
      <div className="grid md:grid-cols-2 gap-4">
        {supportedGroups.map((g, i) => (
          <motion.div key={g} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}>
            <div className="glass rounded-2xl p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gold-400/15 border border-gold-400/35 flex items-center justify-center">
                <Users className="w-5 h-5 text-gold-300" />
              </div>
              <span className="font-inter text-base text-white/85">{g}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Transparency stack */}
    <Section eyebrow="How Funds Flow" title={<>Donor accountability — <span className="italic text-aj-gradient">end to end.</span></>} aurora>
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { t: "Fund Justice Categories", d: "Sponsor women, youth, rural, human rights or public-interest cases." },
          { t: "Eligibility & Approval", d: "AI vetting + admin approval per beneficiary. NGO referrals honored." },
          { t: "Lawyer Engagement", d: "Verified lawyer assigned. Funds released milestone-by-milestone." },
          { t: "Impact Reporting", d: "Beneficiary demographics, outcomes, SDG 16 indicators. Audited annually." },
        ].map((s, i) => (
          <motion.div key={s.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
            <GlassCard glow className="h-full">
              <div className="font-playfair italic text-3xl text-gold-gradient mb-3">0{i + 1}</div>
              <h3 className="font-playfair text-lg text-white mb-2">{s.t}</h3>
              <p className="font-inter text-xs text-white/60 leading-relaxed">{s.d}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Donor benefits */}
    <Section eyebrow="What Donors See" title={<>Live dashboards. <span className="italic text-aj-gradient">Audited outcomes.</span></>}>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Activity, t: "Cases Funded" },
          { icon: BarChart3, t: "Funds Utilization" },
          { icon: Users, t: "Beneficiary Demographics" },
          { icon: FileText, t: "Case-Level Reporting" },
          { icon: Globe2, t: "SDG 16 Metrics" },
          { icon: ShieldCheck, t: "Verified Lawyer Payments" },
          { icon: Wallet, t: "Fund Disbursement Trail" },
          { icon: Heart, t: "Outcomes Achieved" },
        ].map((b, i) => (
          <motion.div key={b.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
            <GlassCard glow className="h-full">
              <b.icon className="w-6 h-6 text-turquoise mb-3" />
              <div className="font-inter text-sm text-white/85">{b.t}</div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Final CTA */}
    <Section className="border-t border-white/8">
      <div className="text-center max-w-3xl mx-auto">
        <Eyebrow>Be part of the change</Eyebrow>
        <h2 className="font-playfair text-4xl md:text-5xl text-white mt-4 mb-8 leading-tight">
          Sponsor a case. <span className="italic text-gold-gradient">Change a life.</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact" data-testid="ngos-final-fund" className="btn-aj-primary">
            Fund the Justice Wallet
          </Link>
          <Link to="/contact" data-testid="ngos-final-partner" className="btn-aj-secondary">
            NGO Partnership
          </Link>
        </div>
      </div>
    </Section>
  </div>
);

export default ForNGOs;
